/**
 * Experience-profile refresh boundary (CC-V35-BANK-DEMO-READY-01).
 *
 * A SAME-organization experience-profile change is a smaller sibling of the
 * §5.8 organization boundary: the organization has not changed, but every
 * organization-scoped read — starting with the `/api/iam/me` bootstrap the
 * profile rides on — must be re-fetched, and every mounted profile consumer
 * must rebuild, in the active session with no logout or manual refresh.
 *
 * Mechanism, in order:
 *  1. advance the organization epoch (`apiCache`) — the ESTABLISHED cache
 *     boundary. Cached org-scoped GETs become unreachable by construction,
 *     exactly as at an organization switch; caches added later inherit the
 *     property without registration.
 *  2. advance the refresh epoch and notify subscribers —
 *     `useExperienceProfile` re-resolves through a pending interlude, so
 *     AppShell / ProfileLanding / BankingProfileRoute rebuild from fresh
 *     server state rather than flashing a stale experience.
 *  3. broadcast to other open tabs of the same browser session:
 *     BroadcastChannel where available, otherwise a localStorage ping (the
 *     `storage` event only fires in OTHER tabs, which is precisely the
 *     needed fan-out). A RECEIVED broadcast applies steps 1–2 locally and
 *     never re-broadcasts, so there is no loop.
 *
 * The ping key carries only a timestamp — device-scoped signal, no
 * organization data — so it is deliberately NOT in the organizationBoundary
 * storage census of org-scoped keys.
 */

import { advanceOrganizationEpoch } from './apiCache';

export const EXPERIENCE_PROFILE_REFRESH_PING_KEY = 'compass_workflow_refresh_ping';

const CHANNEL_NAME = 'compass-experience-profile';

let refreshEpoch = 0;
const subscribers = new Set<() => void>();

export function getExperienceProfileRefreshEpoch(): number {
  return refreshEpoch;
}

export function subscribeExperienceProfileRefresh(listener: () => void): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

function applyLocalRefresh(): void {
  advanceOrganizationEpoch();
  refreshEpoch += 1;
  for (const listener of Array.from(subscribers)) {
    try {
      listener();
    } catch {
      // A failing listener must not starve the others of the boundary.
    }
  }
}

/**
 * Remote-origin refresh: another tab changed the organization's workflow.
 * Applies the local boundary and deliberately never re-broadcasts.
 */
export function receiveExperienceProfileBroadcast(): void {
  applyLocalRefresh();
}

let channel: BroadcastChannel | null = null;
try {
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = () => {
      receiveExperienceProfileBroadcast();
    };
  }
} catch {
  channel = null;
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === EXPERIENCE_PROFILE_REFRESH_PING_KEY) {
      receiveExperienceProfileBroadcast();
    }
  });
}

function broadcastToOtherTabs(): void {
  if (channel) {
    try {
      channel.postMessage('experience-profile-changed');
      return;
    } catch {
      // Fall through to the storage ping.
    }
  }
  try {
    localStorage.setItem(EXPERIENCE_PROFILE_REFRESH_PING_KEY, String(Date.now()));
  } catch {
    // Storage unavailable (private mode) — same-tab refresh already applied,
    // which is the load-bearing half.
  }
}

export interface ExperienceProfileRefreshOptions {
  /** Pass false for remote-origin or test-local refreshes. Default: true. */
  broadcast?: boolean;
}

/**
 * The one entry point after a successful experience-profile mutation:
 * invalidate org-scoped caches via the epoch boundary, re-resolve every
 * mounted profile consumer, and fan out to other open tabs.
 */
export function refreshExperienceProfile(
  options?: ExperienceProfileRefreshOptions
): void {
  applyLocalRefresh();
  if (options?.broadcast === false) return;
  broadcastToOtherTabs();
}
